import { PhasrRecommendationSideMenuView } from '../view/PhasrRecommendationSideMenuView';
import { templatePhasrRecommendationSideMenu } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { PhasrRecommendationSideMenuWebComponent } from '..';
import { PhasrRecommendationSideMenuWebComponentEvents, PhasrRecommendationSideMenuWebComponentSchema } from '../types';
import { PhasrRecommendationSideMenuRepository } from '../repository/PhasrRecommendationSideMenuRepository';
import { DashboardEventsListeners } from '../../../genericDashboard/types/types';
import { PhasrSidePanelComponent } from '../../../phasrSidePanel';
import { translateFn } from '../../../utils/translator.helper';
import { PhasrPanelComponentTypes } from '../../../phasrSidePanel/types/types';
import {
  ActionTakenTypes,
  getPhasrCategoryDictionaryToPrefferedName,
} from '../../../types/common/phasrTypes';
import {
  OPEN_BEHAVIORAL_PROFILES_PANEL,
} from '../../../phasrRecommendations/phasrRecommendationsGrid/types';
import { PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';

export class PhasrRecommendationSideMenuController extends BaseWebComponentController {
  public component: PhasrRecommendationSideMenuWebComponent;
  public view: PhasrRecommendationSideMenuView;
  public viewConstructor: typeof PhasrRecommendationSideMenuView;
  public dataRepository: PhasrRecommendationSideMenuRepository;
  private recommendationData: PhasrRecommendationSideMenuWebComponentSchema;

  constructor(component: PhasrRecommendationSideMenuWebComponent) {
    super(component);
    this.initialSetup({
      dataRepositoryConstructor: PhasrRecommendationSideMenuRepository,
      viewConstructor: PhasrRecommendationSideMenuView,
      viewTemplate: templatePhasrRecommendationSideMenu,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$behavioralProfiles,
        event: OPEN_BEHAVIORAL_PROFILES_PANEL,
        callback: () => this.showBehavioralProfilesPanel(),
      },
      {
        element: this.view.dom.$allowAccessButton,
        event: 'click',
        callback: () => this.allowAccessRecommendation(),
      },
      {
        element: this.view.dom.$restrictAccessButton,
        event: 'click',
        callback: () => this.restrictAccessRecommendation(),
      },
    ];
  }

  private showBehavioralProfilesPanel(): void {
    const sidePanelContent = this.getBehavioralProfilesPanel();
    this.view.dom.$behavioralProfilesPanel.setMenuContent(sidePanelContent);
    this.view.dom.$behavioralProfilesPanel.openSideMenu();
  }

  private getBehavioralProfilesPanel(): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.BehavioralProfilesNoToggle;
    sidePanelContent.hasBackButton = true;
    sidePanelContent.companyId = this.recommendationData.companyId;
    sidePanelContent.componentSpecificData = {
      recommendationId: this.recommendationData.id
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private allowAccessRecommendation(): void {
    const data = this.buildPanelDetails();

    const event = new CustomEvent(PhasrRecommendationSideMenuWebComponentEvents.OnRecommendationAllowAccessFromSidePanel, {
      detail: data,
      bubbles: true,
      composed: true,
    });
    this.component.dispatchEvent(event);

    this.closeSideMenu();
  }

  private restrictAccessRecommendation(): void {
    const data = this.buildPanelDetails();

    const event = new CustomEvent(PhasrRecommendationSideMenuWebComponentEvents.OnRecommendationRestrictAccessFromSidePanel, {
      detail: data,
      bubbles: true,
      composed: true,
    });
    this.component.dispatchEvent(event);

    this.closeSideMenu();
  }

  private buildPanelDetails(): PhasrApplyActionSchema {
    const dictionary = Application.Main.getPhasrDictionary();
    return {
      id: this.recommendationData.id,
      companyId: this.recommendationData.companyId,
      ruleId: this.recommendationData.ruleId,
      description: dictionary[this.recommendationData.ruleId]?.rule_description ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[dictionary[this.recommendationData.ruleId].category] ?? 'N/A',
      rule: this.recommendationData.rule,
      reductionPotential: this.recommendationData.attackSurfaceReduction,
    };
  }

  private closeSideMenu() {
    if (this.component.shouldGetDataFromBackend) {
      this.component.dispatchEvent(new CustomEvent(DashboardEventsListeners.CLOSE_WIDGET_DETAILS, {
        bubbles: true,
        composed: true
      }));
      return;
    }

    this.component.dispatchEvent(new CustomEvent(PhasrRecommendationSideMenuWebComponentEvents.CANCEL, {
      bubbles: true,
      composed: true
    }));
  }

  async getDataForView(): Promise<PhasrRecommendationSideMenuWebComponentSchema> {
    let data: PhasrRecommendationSideMenuWebComponentSchema;
    if (this.component.shouldGetDataFromBackend) {
      const recommendationId = this.component.getRecommendationId();
      data = await this.dataRepository.getRecommendation(recommendationId);
    } else {
      data = this.component.getRecommendationData();
    }

    this.recommendationData = {
      id: data.id ?? '',
      name: data.name ?? '',
      rule: data.rule ?? '',
      ruleId: data.ruleId ?? '',
      targetedActivityType: data.targetedActivityType ?? '',
      createdOn: data.createdOn ?? 0,
      behavioralProfiles: data.behavioralProfiles ?? 0,
      attackSurfaceReduction: data.attackSurfaceReduction ?? 0,
      description: data.description ?? '',
      mitigation: data.mitigation ?? '',
      actionTaken: data.actionTaken ?? ActionTakenTypes.ACTION_NEEDED,
      companyId: data.companyId ?? '',
      type: data.type ?? 0,
      phasrUsable: data.phasrUsable ?? false,
    }

    return this.recommendationData;
  }
}
